import { ErrorHandler, Inject, Injectable } from '@angular/core';

import { InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { SERVICE_WORKER_ENABLED } from '../service-worker-enabled.injection-token';
import { ServiceWorkerMessage } from '../service-worker-message.model';
import { ServiceWorkerMessageService } from '../service-worker-message.service';
import { ServiceWorkerRegistrationHandler } from './service-worker-registration-handlers.injection-token';

// Keep in sync with `activation-telemetry.ts`. We should bump the version if there are major changes in data contract, since
// an installing service worker might not have the same data contract as the app
interface ActivationTelemetryMessagePayloadV1 {
    eventName: KnownInstrumentationEvents;
    params: {
        [key: string]: string;
    };
}

interface ActivationTelemetryMessage extends ServiceWorkerMessage {
    type: 'OFFICECOM_SW_TELEMETRY';
    meta: 'activation';
    payload?: ActivationTelemetryMessagePayloadV1;
}

/**
 * Responsible for handling telemetry from SW
 */
@Injectable()
export class ServiceWorkerActivationTelemetryHandlerService implements ServiceWorkerRegistrationHandler {

    constructor (
        private readonly errorHandler: ErrorHandler,
        private readonly contextService: ContextService,
        private readonly instrumentationService: InstrumentationService,
        @Inject(SERVICE_WORKER_ENABLED) private readonly serviceWorkerEnabled: boolean,
        private readonly serviceWorkerMessageService: ServiceWorkerMessageService
    ) {
    }

    public handleRegistration(registration: ServiceWorkerRegistration) {
        if (!this.serviceWorkerEnabled) {
            return;
        }

        try {
            const serviceWorkerIntervalUpdateEnabled = this.contextService.sharedContext.serviceWorkerIntervalUpdateEnabled;
            const subcribeToActivationTelemetryOnUpdate = () => {
                registration.addEventListener('updatefound', () => {
                    this.subscribeToInstallingServiceWorkerActivationTelemetry(registration.installing);
                });
            };

            if (registration.installing) {
                this.subscribeToInstallingServiceWorkerActivationTelemetry(registration.installing);
                if (serviceWorkerIntervalUpdateEnabled) {
                    subcribeToActivationTelemetryOnUpdate();
                }
            } else {
                subcribeToActivationTelemetryOnUpdate();
            }
        } catch (ex) {
            this.errorHandler.handleError(ex);
        }
    }

    private subscribeToInstallingServiceWorkerActivationTelemetry(installingWorker: ServiceWorker) {
        this.serviceWorkerMessageService.sendTransactionalMessage<ActivationTelemetryMessage>({
            type: 'OFFICECOM_SW_TELEMETRY',
            meta: 'activation'
        } as ActivationTelemetryMessage, installingWorker).then(messageEvent => {
            const message = messageEvent.data as ActivationTelemetryMessage;

            if (message.type !== 'OFFICECOM_SW_TELEMETRY' || message.meta !== 'activation') {
                return;
            }

            this.instrumentationService.logGenericEvent(message.payload.eventName, message.payload.params);
        });
    }
}
